import { useState, useEffect, useRef, useCallback } from 'react';
import { approvalList } from '@/mocks/home';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://nocodb.aibrick.co.kr';
const XC_TOKEN = import.meta.env.VITE_XC_TOKEN || 'FIxhc3kiKgR7TVnzUX8LyXARQSdTjJt0U5AcrOff';
const NOCODB_API_URL = `${API_BASE_URL}/api/v3/data/p32e7pagy9rr8w4/mxbg7xtdv44lmyp/records`;

type ApprovalItem = { date: string; name: string; product: string; status: string };

// ────────────────────────────────────────────────────────────────
// Floating badge + modal that shows real-time approval status
// ────────────────────────────────────────────────────────────────
function ApprovalModal() {
  const [open, setOpen] = useState(false);
  const [liveApprovals, setLiveApprovals] = useState<ApprovalItem[]>([]);
  const tickerRef = useRef<HTMLUListElement | null>(null);

  const fetchLiveApprovals = useCallback(async () => {
    try {
      const response = await fetch(`${NOCODB_API_URL}?limit=30`, {
        headers: { 'xc-token': XC_TOKEN, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        const records = data.records || [];
        const productMap: Record<string, string> = {
          personal: '개인사업자',
          corporate: '법인사업자',
          self: '자영업자',
          enterprise: '기업단기',
          daily: '일수'
        };
        const mapped = records.map((r: any) => {
          const name = r.fields?.name || '';
          const maskedName = name ? name[0] + '**' : '홍**';
          const product = productMap[r.fields?.product] || '개인사업자';
          let dateStr = '26-07-14';
          if (r.fields?.CreatedAt) {
            const d = new Date(r.fields.CreatedAt);
            const yy = String(d.getFullYear()).slice(-2);
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            dateStr = `${yy}-${mm}-${dd}`;
          }
          return { date: dateStr, name: maskedName, product, status: '승인완료' };
        });
        setLiveApprovals(mapped);
      }
    } catch (err) {
      console.error('Failed to fetch live approvals:', err);
    }
  }, []);

  useEffect(() => {
    fetchLiveApprovals();
  }, [fetchLiveApprovals]);

  // Auto-scroll animation inside modal
  useEffect(() => {
    if (!open) return;
    const ticker = tickerRef.current;
    if (!ticker) return;
    let pos = 0;
    const speed = 0.5;
    let raf: number;
    const animate = () => {
      pos -= speed;
      if (Math.abs(pos) >= ticker.scrollHeight / 2) pos = 0;
      ticker.style.transform = `translateY(${pos}px)`;
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [open]);

  const combinedList = [...liveApprovals, ...approvalList].slice(0, 15);
  const duplicatedList = [...combinedList, ...combinedList];
  const todayCount = liveApprovals.length + approvalList.filter(a => a.status === '승인완료').length;

  return (
    <>
      {/* Floating trigger badge – bottom-right corner */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-white border border-background-200 shadow-xl rounded-2xl px-4 py-3 text-sm font-label font-semibold text-foreground-800 hover:shadow-2xl hover:border-primary-300 transition-all duration-300 group"
          style={{ backdropFilter: 'blur(8px)' }}
        >
          <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse" />
          실시간 승인 현황
          <span className="ml-1 text-xs text-primary-500 font-bold">오늘 {todayCount}건</span>
          <span className="ml-1 text-foreground-400 group-hover:translate-x-0.5 transition-transform">›</span>
        </button>
      )}

      {/* Modal overlay */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:justify-end sm:p-6 p-0">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
          />

          {/* Panel */}
          <div className="relative z-10 w-full sm:w-[340px] bg-background-50 rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden"
               style={{ maxHeight: '80vh' }}>
            {/* Header */}
            <div className="p-5 border-b border-background-200/60 flex items-center justify-between bg-white">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse" />
                <span className="text-sm font-label font-semibold text-foreground-800">실시간 승인 현황</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-foreground-500 font-label">오늘 {todayCount}건 승인</span>
                <button
                  onClick={() => setOpen(false)}
                  className="w-8 h-8 rounded-full bg-foreground-800 text-white flex items-center justify-center text-base hover:bg-foreground-900 transition-colors"
                  aria-label="닫기"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Scrolling list */}
            <div className="h-[380px] overflow-hidden relative bg-background-50">
              <ul ref={tickerRef} className="px-4 py-4 space-y-3">
                {duplicatedList.map((item, index) => (
                  <li
                    key={index}
                    className="flex items-center justify-between py-3 px-4 bg-white rounded-xl border border-background-200/50"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-foreground-500 font-label w-16">{item.date}</span>
                      <span className="text-sm font-label font-semibold text-foreground-800 w-12">{item.name}</span>
                      <span className="text-xs text-foreground-600 font-label bg-background-100 px-2 py-1 rounded-md">
                        {item.product}
                      </span>
                    </div>
                    <span
                      className={`text-xs font-label font-bold px-3 py-1 rounded-full ${
                        item.status === '승인완료'
                          ? 'bg-primary-100 text-primary-700'
                          : 'bg-secondary-100 text-secondary-700'
                      }`}
                    >
                      {item.status}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// Main section: heading + consultation form
// ────────────────────────────────────────────────────────────────
export default function ApprovalSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [liveApprovals, setLiveApprovals] = useState<ApprovalItem[]>([]);
  const tickerRef = useRef<HTMLUListElement | null>(null);

  const fetchLiveApprovals = useCallback(async () => {
    try {
      const response = await fetch(`${NOCODB_API_URL}?limit=30`, {
        headers: { 'xc-token': XC_TOKEN, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        const records = data.records || [];
        const productMap: Record<string, string> = {
          personal: '개인사업자',
          corporate: '법인사업자',
          self: '자영업자',
          enterprise: '기업단기',
          daily: '일수'
        };
        const mapped = records.map((r: any) => {
          const name = r.fields?.name || '';
          const maskedName = name ? name[0] + '**' : '홍**';
          const product = productMap[r.fields?.product] || '개인사업자';
          let dateStr = '26-07-14';
          if (r.fields?.CreatedAt) {
            const d = new Date(r.fields.CreatedAt);
            const yy = String(d.getFullYear()).slice(-2);
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            dateStr = `${yy}-${mm}-${dd}`;
          }
          return { date: dateStr, name: maskedName, product, status: '승인완료' };
        });
        setLiveApprovals(mapped);
      }
    } catch (err) {
      console.error('Failed to fetch live approvals:', err);
    }
  }, []);

  useEffect(() => {
    fetchLiveApprovals();
  }, [fetchLiveApprovals]);

  // Auto-scroll ticker inside section
  useEffect(() => {
    const ticker = tickerRef.current;
    if (!ticker) return;
    let pos = 0;
    const speed = 0.5;
    let raf: number;
    const animate = () => {
      pos -= speed;
      if (Math.abs(pos) >= ticker.scrollHeight / 2) pos = 0;
      ticker.style.transform = `translateY(${pos}px)`;
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, []);

  const combinedList = [...liveApprovals, ...approvalList].slice(0, 15);
  const duplicatedList = [...combinedList, ...combinedList];

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setAgreed(false);
      return;
    }

    if (!agreed) {
      setFormError('개인정보취급방침에 동의해 주세요.');
      setFormStatus('error');
      return;
    }

    try {
      const name = (formData.get('name') as string || '').trim();
      const phone = (formData.get('phone') as string || '').trim();
      const amount = formData.get('amount') as string || '';
      const product = formData.get('product') as string || '';

      const response = await fetch(NOCODB_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'xc-token': XC_TOKEN
        },
        body: JSON.stringify({ fields: { name, phone, amount, product } })
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setAgreed(false);
        fetchLiveApprovals();
      } else {
        const errText = await response.text();
        console.error('NocoDB Error:', errText);
        setFormError('상담 신청 등록에 실패했습니다. 다시 시도해 주세요.');
        setFormStatus('error');
      }
    } catch (err) {
      console.error('Network Error:', err);
      setFormError('네트워크 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
      setFormStatus('error');
    }
  }, [agreed, fetchLiveApprovals]);

  return (
    <>
      {/* Floating modal with real-time approvals */}
      <ApprovalModal />

      <section id="consult" className="w-full py-16 md:py-24 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-sm font-label font-semibold text-primary-500 mb-2 tracking-wider uppercase">
              Real-time Approval
            </p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
              "대표님, 저희는 <span className="text-primary-500">매출만</span> 봅니다."
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 font-label">
              대표님께 꼭 맞는 대출상품을 찾아드립니다. 문의전화를 주시면 전문 상담원이 친절한 상담을 해 드립니다.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 max-w-6xl mx-auto">
            {/* Left: inline approval ticker */}
            <div className="flex-1 bg-background-100 rounded-2xl md:rounded-3xl border border-background-200/70 overflow-hidden">
              <div className="p-5 md:p-6 border-b border-background-200/50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse" />
                  <span className="text-sm font-label font-semibold text-foreground-800">실시간 승인 현황</span>
                </div>
                <span className="text-xs text-foreground-500 font-label">
                  오늘 {liveApprovals.length + approvalList.filter(a => a.status === '승인완료').length}건 승인
                </span>
              </div>
              <div className="h-[380px] md:h-[480px] overflow-hidden relative">
                <ul ref={tickerRef} className="p-5 md:p-6 space-y-3">
                  {duplicatedList.map((item, index) => (
                    <li
                      key={index}
                      className="flex items-center justify-between py-3 px-4 bg-white rounded-xl border border-background-200/50"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-foreground-500 font-label w-16">{item.date}</span>
                        <span className="text-sm font-label font-semibold text-foreground-800 w-12">{item.name}</span>
                        <span className="text-xs text-foreground-600 font-label bg-background-100 px-2 py-1 rounded-md">
                          {item.product}
                        </span>
                      </div>
                      <span
                        className={`text-xs font-label font-bold px-3 py-1 rounded-full ${
                          item.status === '승인완료'
                            ? 'bg-primary-100 text-primary-700'
                            : 'bg-secondary-100 text-secondary-700'
                        }`}
                      >
                        {item.status}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right: consultation form */}
            <div className="flex-1">
              <div className="bg-white rounded-2xl md:rounded-3xl border border-background-200/70 p-6 md:p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary-50 rounded-full -translate-y-1/2 translate-x-1/2" />

                <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6 relative z-10">
                  무료 상담 신청하기
                </h3>

                <form data-readdy-form id="loan-consult-form" onSubmit={handleSubmit} className="space-y-4 relative z-10">
                  <div className="honeypot-field">
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-label font-semibold text-foreground-800 mb-1.5">
                      성함(대표자명) <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      placeholder="이름을 입력해주세요."
                      maxLength={10}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-sm font-label text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-label font-semibold text-foreground-800 mb-1.5">
                      연락처(-없이) <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="ex) 01012345678"
                      inputMode="numeric"
                      required
                      pattern="[0-9]{10,11}"
                      className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-sm font-label text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-label font-semibold text-foreground-800 mb-1.5">
                      신청금액
                    </label>
                    <select
                      name="amount"
                      required
                      className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-sm font-label text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all appearance-none cursor-pointer"
                    >
                      <option value="">필요하신 금액을 선택해주세요</option>
                      <option value="1000">1,000만원 이상~</option>
                      <option value="3000">3,000만원 이상~</option>
                      <option value="5000">5,000만원 이상~</option>
                      <option value="10000">1억원 이상~</option>
                      <option value="100000">10억원 이상~</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-label font-semibold text-foreground-800 mb-1.5">
                      대출상품
                    </label>
                    <select
                      name="product"
                      required
                      className="w-full px-4 py-3 rounded-xl border border-background-300/60 bg-background-50 text-sm font-label text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all appearance-none cursor-pointer"
                    >
                      <option value="">원하시는 상품을 선택해주세요</option>
                      <option value="enterprise">기업단기대출</option>
                      <option value="corporate">법인사업자대출</option>
                      <option value="personal">개인사업자대출</option>
                      <option value="self">자영업자대출</option>
                    </select>
                  </div>

                  <div className="flex items-start gap-2 pt-2">
                    <input
                      type="checkbox"
                      id="agree"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      required
                      className="mt-1 w-4 h-4 rounded border-background-300/60 text-primary-500 focus:ring-primary-400 cursor-pointer"
                    />
                    <label htmlFor="agree" className="text-xs font-label text-foreground-600 leading-relaxed cursor-pointer">
                      개인정보취급방침 동의함{' '}
                      <button
                        type="button"
                        onClick={() => document.getElementById('policy-modal')?.classList.remove('hidden')}
                        className="text-primary-500 underline hover:text-primary-600"
                      >
                        [내용보기]
                      </button>
                    </label>
                  </div>

                  {formStatus === 'error' && formError && (
                    <div className="p-3 rounded-xl bg-red-50 text-red-600 text-sm font-label">
                      {formError}
                    </div>
                  )}

                  {formStatus === 'success' && (
                    <div className="p-3 rounded-xl bg-primary-50 text-primary-700 text-sm font-label">
                      상담 신청이 완료되었습니다. 전문 상담원이 빠른 시일 내에 연락드리겠습니다.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-4 rounded-xl bg-primary-500 text-white font-label font-semibold text-sm md:text-base hover:bg-primary-600 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {formStatus === 'loading' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line w-5 h-5 flex items-center justify-center animate-spin" />
                        처리 중...
                      </span>
                    ) : (
                      '무료 상담 신청하기'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}